import { useState } from "react";
import { Filter, Search, X } from "lucide-react";
import { isToday } from "@/lib/filterStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const emptyFilters = {
  q: "", date_from: "", date_to: "", time_from: "", time_to: "",
  name: "", uhid: "", sample_id: "", status: "", test_name: "", analyzer: "", priority: "",
};

export const filterParams = (f) =>
  Object.fromEntries(Object.entries(f || {}).filter(([, v]) => String(v || "").trim()));

const LBL = "text-[10px] uppercase tracking-[0.1em] text-[#6B7280]";

/** Shared date/time, name, ULID and sample-wise filter bar used across every section. */
export const FilterBar = ({ value, onChange, onApply, selects = {}, testid = "filters" }) => {
  const [open, setOpen] = useState(false);
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const active = Object.entries(value || {}).filter(([k, v]) => k !== "q" && String(v || "").trim()).length;
  const submit = (e) => { e.preventDefault(); onApply?.(); };

  return (
    <form onSubmit={submit} className="border-b border-[#E5E7EB]" data-testid={testid}>
      <div className="p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[180px]">
          <Search className="h-4 w-4 text-[#6B7280] shrink-0" />
          <input value={value.q} onChange={set("q")} data-testid="search-input"
            placeholder="Search any field…" className="flex-1 min-w-0 text-sm outline-none bg-transparent" />
        </div>
        <Button type="button" variant="outline" size="sm" data-testid="filters-toggle"
          onClick={() => setOpen((o) => !o)}>
          <Filter className="h-3.5 w-3.5 mr-1.5 text-[#002FA7]" /> Filters
          {active > 0 && (
            <span className="ml-1.5 h-4 min-w-4 px-1 rounded-full bg-[#002FA7] text-white text-[10px]
              inline-flex items-center justify-center" data-testid="filters-count">{active}</span>
          )}
        </Button>
        <Button type="submit" size="sm" data-testid="filters-apply"
          className="bg-[#002FA7] hover:bg-[#00237C] text-white">Apply</Button>
      </div>

      {isToday(value) && (
        <div className="px-3 pb-2 -mt-1" data-testid="today-banner">
          <p className="text-[11px] text-[#6B7280]">
            Showing <strong className="text-[#111827]">today only</strong> (00:00–23:59).
            Change the dates or clear the filters to see everything.
          </p>
        </div>
      )}

      {open && (
        <div className="px-3 pb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="filters-panel">
          <div>
            <p className={LBL}>Date from</p>
            <Input type="date" value={value.date_from} onChange={set("date_from")}
              className="mt-1 h-9" data-testid="filter-date-from" />
          </div>
          <div>
            <p className={LBL}>Date to</p>
            <Input type="date" value={value.date_to} onChange={set("date_to")}
              className="mt-1 h-9" data-testid="filter-date-to" />
          </div>
          <div>
            <p className={LBL}>Time from</p>
            <Input type="time" value={value.time_from} onChange={set("time_from")}
              className="mt-1 h-9" data-testid="filter-time-from" />
          </div>
          <div>
            <p className={LBL}>Time to</p>
            <Input type="time" value={value.time_to} onChange={set("time_to")}
              className="mt-1 h-9" data-testid="filter-time-to" />
          </div>
          <div>
            <p className={LBL}>Name</p>
            <Input value={value.name} onChange={set("name")} placeholder="Patient, staff or item"
              className="mt-1 h-9" data-testid="filter-name" />
          </div>
          <div>
            <p className={LBL}>ULID / Patient ID</p>
            <Input value={value.uhid} onChange={set("uhid")} placeholder="e.g. LTBP-ABC-2608270001"
              className="mt-1 h-9" data-testid="filter-uhid" />
          </div>
          <div>
            <p className={LBL}>Sample ID</p>
            <Input value={value.sample_id} onChange={set("sample_id")} placeholder="e.g. S-2026-0912"
              className="mt-1 h-9" data-testid="filter-sample-id" />
          </div>
          {Object.entries(selects).map(([key, opts]) => (
            <div key={key}>
              <p className={LBL}>{key.replace(/_/g, " ")}</p>
              <select value={value[key] || ""} onChange={set(key)} data-testid={`filter-${key}`}
                className="mt-1 h-9 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-sm">
                <option value="">All</option>
                {(opts || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          ))}
          <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
            <Button type="button" variant="ghost" size="sm" data-testid="filters-clear"
              onClick={() => { onChange({ ...emptyFilters }); onApply?.({ ...emptyFilters }); }}>
              <X className="h-3.5 w-3.5 mr-1" /> Clear all filters
            </Button>
          </div>
        </div>
      )}
    </form>
  );
};
